import { useState } from 'react';
import moment from 'moment-timezone';

import { notifySuccess } from '@/portainer/services/notifications';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../types';
import {
  PolicyConfigurationFormSection,
  PolicyConfigurationValues,
} from '../components/PolicyConfigurationFormSection';
import { DockerSecurityPolicyForm } from '../components/docker/security/DockerSecurityPolicyForm';
import { buildDockerSecurityPayload } from '../components/docker/security/buildDockerSecurityPayload';
import { DockerSetupPolicyForm } from '../components/docker/setup/DockerSetupPolicyForm';
import { buildDockerSetupPayload } from '../components/docker/setup/buildDockerSetupPayload';
import { DockerRBACPolicyForm } from '../components/docker/rbac/DockerRBACPolicyForm';
import { DockerRegistryPolicyForm } from '../components/docker/registry/DockerRegistryPolicyForm';
import { KubernetesRBACPolicyForm } from '../components/kubernetes/rbac/KubernetesRBACPolicyForm';
import { buildKubernetesRBACPayload } from '../components/kubernetes/rbac/buildKubernetesRBACPayload';
import { buildDockerRBACPayload } from '../components/docker/rbac/buildDockerRBACPayload';
import { buildDockerRegistryPayload } from '../components/docker/registry/buildDockerRegistryPayload';
import { useDockerRBACInitialValues } from '../components/docker/rbac/useDockerRBACInitialValues';
import { useDockerSecurityInitialValues } from '../components/docker/security/useDockerSecurityInitialValues';
import { useDockerSetupInitialValues } from '../components/docker/setup/useDockerSetupInitialValues';
import { useDockerRegistryInitialValues } from '../components/docker/registry/useDockerRegistryInitialValues';
import { useKubernetesRBACInitialValues } from '../components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { KubernetesSecurityPolicyForm } from '../components/kubernetes/security/KubernetesSecurityPolicyForm';
import { useKubernetesSecurityInitialValues } from '../components/kubernetes/security/useKubernetesSecurityInitialValues';
import { buildKubernetesSecurityPayload } from '../components/kubernetes/security/buildKubernetesSecurityPayload';
import { KubernetesSetupPolicyForm } from '../components/kubernetes/setup/KubernetesSetupPolicyForm';
import { useKubernetesSetupInitialValues } from '../components/kubernetes/setup/useKubernetesSetupInitialValues';
import { buildKubernetesSetupPayload } from '../components/kubernetes/setup/buildKubernetesSetupPayload';
import { KubernetesRegistryPolicyForm } from '../components/kubernetes/registry/KubernetesRegistryPolicyForm';
import { useKubernetesRegistryInitialValues } from '../components/kubernetes/registry/useKubernetesRegistryInitialValues';
import { buildKubernetesRegistryPayload } from '../components/kubernetes/registry/buildKubernetesRegistryPayload';
import { useUpdatePolicyMutation } from '../queries/useUpdatePolicyMutation';
import { CreatePolicyData } from '../queries/types';
import { useConfirmDistributePolicyModal } from '../components/useConfirmDistributePolicyModal';

interface Props {
  policy: TypedPolicy<unknown>;
}

export function ConfigTab({ policy }: Props) {
  const [timeZone, setTimeZone] = useState<string>(moment.tz.guess());
  const [policyConfig, setPolicyConfig] = useState<PolicyConfigurationValues>(
    () => ({
      name: policy.Name || '',
      type: policy.Type,
      environmentGroupIds: policy.EnvironmentGroups || [],
    })
  );

  const updateMutation = useUpdatePolicyMutation();
  const confirmDistributePolicyModal = useConfirmDistributePolicyModal(
    policyConfig.name,
    policyConfig.environmentGroupIds
  );

  const dockerSecurityInitial = useDockerSecurityInitialValues(policy);
  const dockerSetupInitial = useDockerSetupInitialValues(policy);
  const dockerRbacInitial = useDockerRBACInitialValues(policy);
  const dockerRegistryInitial = useDockerRegistryInitialValues(policy);
  const k8sSecurityInitial = useKubernetesSecurityInitialValues(policy);
  const k8sSetupInitial = useKubernetesSetupInitialValues(policy);
  const k8sRbacInitial = useKubernetesRBACInitialValues(policy);
  const k8sRegistryInitial = useKubernetesRegistryInitialValues(policy);

  if (!policy.Name) {
    return null;
  }

  const environmentGroupsProvided = !!policyConfig.environmentGroupIds.length;

  return (
    <div className="form-horizontal">
      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody>
              <div className="form-group">
                <div className="col-sm-12">
                  <PolicyConfigurationFormSection
                    onChange={setPolicyConfig}
                    policyConfiguration={policyConfig}
                    environmentType={policy.EnvironmentType}
                    disableCategory
                  />

                  {/* Type-specific forms - only render active form */}
                  {/* Docker/Swarm/Podman */}
                  {policyConfig.type === 'security-docker' && (
                    <DockerSecurityPolicyForm
                      initialValues={dockerSecurityInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildDockerSecurityPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}

                  {policyConfig.type === 'setup-docker' && (
                    <DockerSetupPolicyForm
                      initialValues={dockerSetupInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildDockerSetupPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                      timeZone={timeZone}
                      onChangeTimeZone={setTimeZone}
                    />
                  )}

                  {policyConfig.type === 'rbac-docker' && (
                    <DockerRBACPolicyForm
                      initialValues={dockerRbacInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildDockerRBACPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}

                  {policyConfig.type === 'registry-docker' && (
                    <DockerRegistryPolicyForm
                      initialValues={dockerRegistryInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildDockerRegistryPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}

                  {/* Kubernetes */}
                  {policyConfig.type === 'security-k8s' && (
                    <KubernetesSecurityPolicyForm
                      initialValues={k8sSecurityInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildKubernetesSecurityPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}

                  {policyConfig.type === 'setup-k8s' && (
                    <KubernetesSetupPolicyForm
                      initialValues={k8sSetupInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildKubernetesSetupPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                      timeZone={timeZone}
                      onChangeTimeZone={setTimeZone}
                    />
                  )}

                  {policyConfig.type === 'rbac-k8s' && (
                    <KubernetesRBACPolicyForm
                      initialValues={k8sRbacInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildKubernetesRBACPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}

                  {policyConfig.type === 'registry-k8s' && (
                    <KubernetesRegistryPolicyForm
                      initialValues={k8sRegistryInitial}
                      onSubmit={(values) =>
                        handleSubmit(values, buildKubernetesRegistryPayload)
                      }
                      nameProvided={!!policyConfig.name.trim()}
                      environmentGroupsProvided={environmentGroupsProvided}
                      mode="edit"
                    />
                  )}
                </div>
              </div>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </div>
  );

  async function handleSubmit<TFormData, TPayload extends CreatePolicyData>(
    formData: TFormData,
    payloadBuilder: (formData: TFormData) => TPayload
  ) {
    const confirmed = await confirmDistributePolicyModal();
    if (!confirmed) {
      return;
    }

    await updateMutation.mutateAsync(
      {
        id: policy.Id,
        payload: {
          Name: policyConfig.name,
          Type: policyConfig.type,
          EnvironmentType: policy.EnvironmentType,
          EnvironmentGroups: policyConfig.environmentGroupIds,
          Data: payloadBuilder(formData),
        },
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Policy updated successfully');
        },
      }
    );
  }
}
